import React from 'react';
import { Modal, Stack, TextInput, Select, Group, Text, Box } from '@mantine/core';
import { IconLicense, IconShield, IconSettings, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IAppLicense, FeatureFlag, DEFAULT_FEATURE_FLAGS } from '@/types/App.model';
import { EditorChipButton } from '@/components/EditorChipButton';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';

interface LicenseModalProps {
  opened: boolean;
  onClose: () => void;
  editingLicense: IAppLicense | null;
  licenseFormData: {
    appId: string;
    licenseName: string;
    featureFlags: Record<string, FeatureFlag>;
  };
  onLicenseFormDataChange: (data: {
    appId: string;
    licenseName: string;
    featureFlags: Record<string, FeatureFlag>;
  }) => void;
  onSave: () => void;
  apps: any[];
  accentColor?: string;
}

export function LicenseModal({
  opened,
  onClose,
  editingLicense,
  licenseFormData,
  onLicenseFormDataChange,
  onSave,
  apps,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: LicenseModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconLicense size={20} color="#10b981" />
          <Text fw={600}>
            {editingLicense ? t('masterAdmin.licenses.update') : t('masterAdmin.licenses.create')}
          </Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label={t('masterAdmin.licenses.name')}
          placeholder={t('masterAdmin.licenses.namePlaceholder')}
          value={licenseFormData.licenseName}
          onChange={(e) =>
            onLicenseFormDataChange({ ...licenseFormData, licenseName: e.target.value })
          }
          required
          leftSection={<IconLicense size={16} />}
        />
        <Select
          label={t('masterAdmin.apps.select')}
          placeholder={t('masterAdmin.apps.select')}
          value={licenseFormData.appId}
          onChange={(value) => onLicenseFormDataChange({ ...licenseFormData, appId: value || '' })}
          data={apps.map((app) => ({ value: app._id, label: app.appName }))}
          required
          leftSection={<IconShield size={16} />}
        />

        <Box>
          <Group mb="xs">
            <IconSettings size={16} color="#6b7280" />
            <Text size="sm" fw={500}>
              {t('masterAdmin.licenses.featureFlags')}
            </Text>
          </Group>
          <Stack gap="xs">
            {Object.entries(DEFAULT_FEATURE_FLAGS).map(([key, defaultValue]) => (
              <Group key={key} justify="space-between" align="center">
                <Text size="sm" style={{ flex: 1 }}>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </Text>
                <Select
                  size="xs"
                  style={{ width: 120 }}
                  value={licenseFormData.featureFlags[key] || defaultValue}
                  onChange={(value) =>
                    onLicenseFormDataChange({
                      ...licenseFormData,
                      featureFlags: {
                        ...licenseFormData.featureFlags,
                        [key]: (value as FeatureFlag) || defaultValue,
                      },
                    })
                  }
                  data={[
                    { value: FeatureFlag.ENABLED, label: 'Enabled' },
                    { value: FeatureFlag.DISABLED, label: 'Disabled' },
                    { value: FeatureFlag.UNDEFINED, label: 'Undefined' },
                  ]}
                />
              </Group>
            ))}
          </Stack>
        </Box>

        <Group justify="flex-end" mt="md" gap={8}>
          <EditorChipButton accentColor={accentColor} onClick={onClose}>
            {t('masterAdmin.forms.cancel')}
          </EditorChipButton>
          <EditorChipButton
            variant="primary"
            accentColor={accentColor}
            leftIcon={<IconCheck size={15} stroke={1.75} />}
            onClick={onSave}
          >
            {editingLicense ? t('masterAdmin.licenses.update') : t('masterAdmin.licenses.create')}
          </EditorChipButton>
        </Group>
      </Stack>
    </Modal>
  );
}
